import type { ConfiguratorRuntimeDocument } from "./ConfiguratorRuntimeDocument";

export type ConfiguratorDocumentHistoryState = {
  canUndo: boolean;
  canRedo: boolean;
  loading: boolean;
};

const MAX_DOCUMENTS = 21;

export class ConfiguratorDocumentHistory {
  private documents: ConfiguratorRuntimeDocument[] = [];
  private index = -1;
  private loading = false;

  constructor(
    private readonly load: (
      document: ConfiguratorRuntimeDocument,
    ) => Promise<void>,
    private readonly onChange: (
      state: ConfiguratorDocumentHistoryState,
    ) => void,
  ) {}

  initialize(document: ConfiguratorRuntimeDocument): void {
    this.documents = [cloneDocument(document)];
    this.index = 0;
    this.emit();
  }

  commit(document: ConfiguratorRuntimeDocument): void {
    const next = cloneDocument(document);
    const current = this.documents[this.index];
    if (current && documentsEqual(current, next)) return;
    this.documents.splice(this.index + 1);
    this.documents.push(next);
    while (this.documents.length > MAX_DOCUMENTS) {
      this.documents.shift();
    }
    this.index = this.documents.length - 1;
    this.emit();
  }

  get state(): ConfiguratorDocumentHistoryState {
    return {
      canUndo: this.index > 0,
      canRedo: this.index >= 0 && this.index < this.documents.length - 1,
      loading: this.loading,
    };
  }

  async undo(): Promise<void> {
    if (!this.state.canUndo || this.loading) return;
    await this.goTo(this.index - 1);
  }

  async redo(): Promise<void> {
    if (!this.state.canRedo || this.loading) return;
    await this.goTo(this.index + 1);
  }

  private async goTo(index: number): Promise<void> {
    const document = this.documents[index];
    if (!document) return;
    this.loading = true;
    this.emit();
    try {
      await this.load(cloneDocument(document));
      this.index = index;
    } finally {
      this.loading = false;
      this.emit();
    }
  }

  private emit(): void {
    this.onChange(this.state);
  }
}

function cloneDocument(
  document: ConfiguratorRuntimeDocument,
): ConfiguratorRuntimeDocument {
  return structuredClone(document);
}

function documentsEqual(
  first: ConfiguratorRuntimeDocument,
  second: ConfiguratorRuntimeDocument,
): boolean {
  return JSON.stringify(first) === JSON.stringify(second);
}
